/**
 * The places this browser has actually delivered from and to.
 *
 * Written only after an order goes through, read when the next one starts. The whole point
 * is the second order: the customer's house does not move, and asking them to find it on a
 * map again is the single hardest thing this app asks anybody to do — hard enough that the
 * owner said it of himself, not only of the elderly customers it was reported for.
 *
 * **This is not the saved-places list that used to exist.** That one was a section on the
 * screen, and it was removed on purpose — see `PlacePicker.jsx`, *"Saved places went
 * entirely."* Four ways to answer *where*, stacked, turned a step whose whole premise is
 * having one thing on it into a screen the customer had to survey first. That judgement
 * still holds. What comes back is the data, not the section: the dropoff arrives already
 * filled in, and the pickup arrives as suggestions inside the search box that is already
 * there. Neither adds a control.
 *
 * Nothing here touches the network or the server. A remembered place is a convenience
 * belonging to one browser, and it dies with the browser — see `clearPlaces`, which
 * sign-out calls, because a cheap Android in a Laguna household is a shared cheap Android.
 */

/**
 * Payload version, not key version.
 *
 * The plan called for `machi.places.v1` as the key. This is better by a little: a version
 * inside the payload can be *read* and decided about, where a version in the key can only
 * be missed. Today the decision is always "discard" — a remembered place is cheap and the
 * customer regenerates it by ordering — but a future shape change can migrate instead, and
 * a key-versioned store cannot even see that it has old data to migrate.
 */
const KEY = 'machi.places'
const VERSION = 1

/** Five each. In a town this size the third is already rare, and the fifth is decoration. */
const LIMIT = 5

const KINDS = ['pickup', 'dropoff']

/**
 * Four decimals, about eleven metres.
 *
 * Two answers to *where is your house* never carry identical coordinates — a pin dropped by
 * hand lands a few metres from the one the search returned, and the search itself moves
 * between a rooftop and a gate. Deduping on the label instead would be worse: the same
 * house is *"Blk 4 Lot 12"* one week and *"bahay"* the next, and both are true.
 */
const PRECISION = 4

const round = (value) => Math.round(value * 10 ** PRECISION) / 10 ** PRECISION

/** Two entries are the same place when they land within about eleven metres. */
const keyOf = (place) => `${round(place.lat)},${round(place.lng)}`

/**
 * Reading `localStorage` can itself throw before anybody calls a method on it.
 *
 * Some Android WebViews and every browser with site data blocked raise on the property
 * access, not on `getItem`. A module that assumed otherwise would take the wizard down at
 * import time on exactly the phones this feature was written for.
 */
function defaultStorage() {
  try {
    return globalThis.localStorage ?? null
  } catch {
    return null
  }
}

/**
 * The customer's own number, kept beside the places rather than in a file of its own.
 *
 * It is not a place, and the name on the module is honest about places — but it is the same
 * fact about the same browser, written at the same moment, wiped by the same sign-out. A
 * second module would be a second key, a second parse, a second thing for `clearPlaces` to
 * remember to call, and a second chance to forget one of them.
 *
 * `phoneNumber` is the field the wizard asks for on the dropoff step and the only thing on
 * the whole form a returning customer still has to type — `payload.js` notes that nothing
 * the account already knows is asked for, and this is the exception it could not cover,
 * because Google hands over a name and an email and never a mobile number.
 */
const emptyContact = () => ({ phoneNumber: '', dropoffContactName: '' })

const empty = () => ({ pickup: [], dropoff: [], contact: emptyContact() })

/**
 * A place is its pin. Everything else on it is a caption.
 *
 * An entry without usable coordinates is not a weaker record, it is a useless one: it would
 * fill a slot in a five-item list, offer the customer a row to tap, and put nothing in the
 * field when they tapped it. Refused on the way in and ignored on the way out, because a
 * store written by an older build of this app is not something this one can assume about.
 */
/**
 * `Number(null)` is `0`, and `0` is finite.
 *
 * `makeEmptyLocation()` starts every location as `{ lat: null, lng: null }`, so a plain
 * `Number()` followed by `Number.isFinite` accepts an unanswered location as a pin in the
 * Gulf of Guinea — the one coordinate on earth that is never anybody's house, offered to
 * the customer as somewhere they have been before. `Number('')` does the same thing.
 *
 * Written as a rejection of the two values that lie rather than a whitelist of the ones
 * that do not, because that is the shape the bug had.
 */
const toCoord = (value) => {
  if (value === null || value === undefined || value === '') return NaN

  return Number(value)
}

function sanitize(place) {
  const lat = toCoord(place?.lat)
  const lng = toCoord(place?.lng)

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null

  const label = String(place?.label ?? '').trim()
  const name = String(place?.name ?? '').trim()

  return name ? { lat, lng, label, name } : { lat, lng, label }
}

function readAll(storage) {
  if (!storage) return empty()

  let raw = null

  try {
    raw = storage.getItem(KEY)
  } catch {
    return empty()
  }

  if (!raw) return empty()

  let parsed = null

  try {
    parsed = JSON.parse(raw)
  } catch {
    return empty()
  }

  if (!parsed || parsed.v !== VERSION) return empty()

  const out = empty()

  for (const kind of KINDS) {
    const rows = Array.isArray(parsed[kind]) ? parsed[kind] : []
    out[kind] = rows.map(sanitize).filter(Boolean).slice(0, LIMIT)
  }

  out.contact = {
    phoneNumber: String(parsed.contact?.phoneNumber ?? '').trim(),
    dropoffContactName: String(parsed.contact?.dropoffContactName ?? '').trim(),
  }

  return out
}

function writeAll(all, storage) {
  if (!storage) return

  try {
    storage.setItem(KEY, JSON.stringify({ v: VERSION, ...all }))
  } catch {
    // A full or refused store is not an error the customer can act on, and the order they
    // are in the middle of does not depend on this succeeding.
  }
}

/**
 * The remembered places for one end of the trip, newest first.
 *
 * Always an array. Every failure this can have — no storage, refused storage, no data,
 * damaged data, data from a shape this build does not know — returns an empty one, because
 * every caller's correct behaviour in all five cases is identical: carry on with nothing
 * remembered, which is exactly how the app behaved before this file existed.
 *
 * @param {'pickup'|'dropoff'} kind
 * @returns {Array<{lat: number, lng: number, label: string, name?: string}>}
 */
export function readPlaces(kind, storage = defaultStorage()) {
  if (!KINDS.includes(kind)) return []

  return readAll(storage)[kind]
}

/**
 * Put a place at the front of its list.
 *
 * Called after an order is placed, never while one is being typed. A place the customer
 * entered and then abandoned has not been shown to be real — the order may have died
 * because that address was wrong — and a list built from abandoned attempts offers the
 * customer their own mistakes back.
 *
 * Re-remembering a place already held **moves it to the front rather than adding a second
 * row**, and the newer copy wins: same house, better label.
 *
 * @param {'pickup'|'dropoff'} kind
 * @param {{lat: number, lng: number, label?: string, name?: string}} place
 */
export function rememberPlace(kind, place, storage = defaultStorage()) {
  if (!KINDS.includes(kind)) return

  const clean = sanitize(place)

  if (!clean) return

  const all = readAll(storage)
  const rest = all[kind].filter((held) => keyOf(held) !== keyOf(clean))

  all[kind] = [clean, ...rest].slice(0, LIMIT)

  writeAll(all, storage)
}

/**
 * Rename a place the customer has used before.
 *
 * The label a place arrives with is whatever answered the search — *"Batong Malake, Los
 * Baños"*, or a line of coordinates for a pin dropped by hand. Neither is what the person
 * living there calls it, and the label is what the rider is read out on arrival.
 *
 * Matched on the pin, not on the old label, because the label is the thing being changed.
 * An empty name is allowed and means *no name*: the picker falls back to the coordinates,
 * which is where it started.
 *
 * @param {'pickup'|'dropoff'} kind
 * @param {{lat: number, lng: number}} place — identified by its pin, to eleven metres
 * @param {string} label
 */
export function renamePlace(kind, place, label, storage = defaultStorage()) {
  if (!KINDS.includes(kind)) return

  const target = sanitize(place)

  if (!target) return

  const all = readAll(storage)
  const found = all[kind].find((held) => keyOf(held) === keyOf(target))

  if (!found) return

  const text = String(label ?? '').trim()

  found.label = text

  /**
   * A pickup carries its name as well, and the two must not drift apart.
   *
   * `name` is what fills *"Pangalan ng tindahan o lugar"* on the step and what the rider is
   * told to ask for at the counter; `label` is what the row in the dropdown reads. Writing
   * only one of them would let a customer rename a shop, watch the row change, and then be
   * handed the old name by the very tap they made the change for.
   *
   * Dropoffs have no `name` — their label is the whole of what the partner's screen and the
   * rider's phone are given — so there is nothing to keep in step.
   */
  if (kind === 'pickup') found.name = text

  writeAll(all, storage)
}

/**
 * Forget one place, without touching the rest.
 *
 * The list is five long and fills itself from real orders, so the way a wrong entry leaves
 * is by being pushed off the end — eventually, by four other deliveries. That is a long
 * time to be offered somewhere you went once by mistake, or an address you have moved away
 * from, every time you open the form.
 *
 * @param {'pickup'|'dropoff'} kind
 * @param {{lat: number, lng: number}} place
 */
export function forgetPlace(kind, place, storage = defaultStorage()) {
  if (!KINDS.includes(kind)) return

  const target = sanitize(place)

  if (!target) return

  const all = readAll(storage)

  all[kind] = all[kind].filter((held) => keyOf(held) !== keyOf(target))

  writeAll(all, storage)
}

/**
 * The customer's own number and the name to put at the dropoff, as last sent.
 *
 * Always an object with both fields present as strings, for the same reason `readPlaces`
 * always returns an array: the caller seeds a form with it, and a form field seeded with
 * `undefined` is an uncontrolled input that React complains about at the moment the
 * customer first types into it.
 *
 * @returns {{phoneNumber: string, dropoffContactName: string}}
 */
export function readContact(storage = defaultStorage()) {
  return readAll(storage).contact
}

/**
 * Remember the number an order actually went out with.
 *
 * Blank fields are kept as blanks rather than skipped. A customer who cleared the name on
 * their last order meant to clear it, and restoring the older one on the next form would be
 * the app arguing with them.
 *
 * @param {{phoneNumber?: string, dropoffContactName?: string}} contact
 */
export function rememberContact(contact, storage = defaultStorage()) {
  const all = readAll(storage)

  all.contact = {
    phoneNumber: String(contact?.phoneNumber ?? '').trim(),
    dropoffContactName: String(contact?.dropoffContactName ?? '').trim(),
  }

  writeAll(all, storage)
}

/**
 * Forget everything — both lists and the contact.
 *
 * Sign-out calls this. The list holds somebody's house and the shops they buy from, and the
 * phone it is written on is borrowed as often as it is owned. Leaving it for the next person
 * to sign in on the same handset is the kind of thing nobody notices until it matters.
 */
export function clearPlaces(storage = defaultStorage()) {
  if (!storage) return

  try {
    storage.removeItem(KEY)
  } catch {
    // Nothing to do and nothing to tell the customer.
  }
}
